import { Markdown } from '@cadstrom/sigma-engine-shared';

import { PdfCitation } from './pdf-citation.js';

import type { ReactNode } from 'react';

export interface PdfCitationMarkdownItemProps {
	citationText?: string;
	fileId?: string;
	elementIndex?: string | number;
	pageNumber?: string | number;
	citedText?: string;
	description?: string;
	children?: ReactNode;
}

/** Turns a sanitized `:pdf-citation[…]` directive into an interactive citation. */
export function PdfCitationMarkdownItem({
	citationText,
	fileId,
	elementIndex: rawElementIndex,
	pageNumber: rawPageNumber,
	citedText,
	description,
	children,
}: PdfCitationMarkdownItemProps) {
	const content = citationText ?? children;
	const elementIndexes = Markdown.parsePdfCitationElementIndexes(rawElementIndex);
	const pageNumber = parseInteger(rawPageNumber, 1);
	const normalizedCitedText = citedText?.trim();

	if (content === undefined || fileId === undefined || fileId.trim() === '') {
		return content;
	}

	if (elementIndexes !== null) {
		return (
			<PdfCitation
				fileId={fileId}
				elementIndexes={elementIndexes}
				pageNumber={pageNumber}
				citedText={normalizedCitedText}
				description={description}
				appearance='underlined'
			>
				{content}
			</PdfCitation>
		);
	}

	if (normalizedCitedText) {
		return (
			<PdfCitation
				fileId={fileId}
				pageNumber={pageNumber}
				citedText={normalizedCitedText}
				description={description}
				appearance='underlined'
			>
				{content}
			</PdfCitation>
		);
	}

	if (pageNumber !== undefined) {
		return (
			<PdfCitation fileId={fileId} pageNumber={pageNumber} description={description} appearance='underlined'>
				{content}
			</PdfCitation>
		);
	}

	return content;
}

function parseInteger(value: string | number | undefined, minimum: number): number | undefined {
	if (typeof value === 'number') {
		return Number.isInteger(value) && value >= minimum ? value : undefined;
	}

	if (value === undefined || !/^\d+$/.test(value.trim())) {
		return undefined;
	}

	const parsed = Number(value);
	return Number.isSafeInteger(parsed) && parsed >= minimum ? parsed : undefined;
}
